import { f0FormField, RenderIfCondition } from '@factorialco/f0-react'
import { F0SelectItemObject } from '@factorialco/f0-react'
import { z } from 'zod'

import { PermissionGroupRecord, usePermissionGroupSelectorSource } from './source'

type BasePermissionGroupSelectorFormFieldParams = {
  label: string
  section?: string
  placeholder?: string
  helpText?: string
  renderIf?: RenderIfCondition
}

const mapPermissionGroupOptions = (
  group: PermissionGroupRecord
): F0SelectItemObject<string, PermissionGroupRecord> => ({
  value: group.id,
  label: group.name,
  item: group,
})

const useOptionalSinglePermissionGroupSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BasePermissionGroupSelectorFormFieldParams) => {
  const source = usePermissionGroupSelectorSource()

  return f0FormField(z.string().optional(), {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    source,
    mapOptions: mapPermissionGroupOptions,
  })
}

const useMandatorySinglePermissionGroupSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BasePermissionGroupSelectorFormFieldParams) => {
  const source = usePermissionGroupSelectorSource()

  return f0FormField(z.string(), {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    source,
    mapOptions: mapPermissionGroupOptions,
  })
}

const useOptionalMultiPermissionGroupSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BasePermissionGroupSelectorFormFieldParams) => {
  const source = usePermissionGroupSelectorSource()

  return f0FormField(z.array(z.string()).optional(), {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    multiple: true,
    source,
    mapOptions: mapPermissionGroupOptions,
  })
}

const useMandatoryMultiPermissionGroupSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
}: BasePermissionGroupSelectorFormFieldParams) => {
  const source = usePermissionGroupSelectorSource()

  return f0FormField(z.array(z.string()).min(1), {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    multiple: true,
    source,
    mapOptions: mapPermissionGroupOptions,
  })
}

type SinglePermissionGroupSelectorFormFieldParams<M extends boolean> =
  BasePermissionGroupSelectorFormFieldParams & {
    mandatory: M
  }

export const useSinglePermissionGroupSelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: SinglePermissionGroupSelectorFormFieldParams<M>) => {
  const mandatoryField = useMandatorySinglePermissionGroupSelectorFormField({ ...rest, renderIf })
  const optionalField = useOptionalSinglePermissionGroupSelectorFormField({ ...rest, renderIf })

  return mandatory ? mandatoryField : optionalField
}

type MultiPermissionGroupSelectorFormFieldParams<M extends boolean> =
  BasePermissionGroupSelectorFormFieldParams & {
    mandatory: M
  }

const useMultiPermissionGroupSelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: MultiPermissionGroupSelectorFormFieldParams<M>) => {
  const mandatoryField = useMandatoryMultiPermissionGroupSelectorFormField({ ...rest, renderIf })
  const optionalField = useOptionalMultiPermissionGroupSelectorFormField({ ...rest, renderIf })

  return mandatory ? mandatoryField : optionalField
}
